import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { StarRatingDisplay } from '@nexus/fff-ratings-and-reviews';

import { getVariantData } from 'utils/subscriptions';
import withSelectedVariant from 'HOC/withSelectedVariant/withSelectedVariant';
import Gallery from 'Scenes/Products/Gallery/Gallery';
import Variants from 'Scenes/Products/Variants/Variants';
import SubscribableDetails from '../SubscribableDetails/SubscribableDetails';
import ProductReviewCTA from '../ProductReviewCTA';
import SubscriptionBadge from 'Scenes/Products/SubscriptionBadge/SubscriptionBadge';
import NonSubscribableDetails from '../NonSubscribableDetails/NonSubscribableDetails';
import styles from '../ProductDetail.module.css';

export const ProductSummary = ({
  productName,
  productRatingData,
  brandName,
  variants,
  selectedVariant,
  selectedDescriptors,
  updateSelectedVariant,
  updateSelectedDescriptors,
  loggedIn,
  viewSize,
  campaignProductData,
  rateAndReviewFF,
  planPrice,
  shippingRestrictions,
  ctaElementRef,
  hasProductReviewed,
  reviewsSectionRef,
  isParisHiltonKit,
  variantIdsBeingUpdated,
  addToPublicCart,
}) => {
  const { id, galleryUrls, isSubscriptionAvailable } = selectedVariant;
  const { retailPrice, salePrice, percentOff, soldOut, sku } = getVariantData(
    selectedVariant,
    campaignProductData,
    isSubscriptionAvailable
  );
  const isPublicShop =
    campaignProductData?.subscribable || isSubscriptionAvailable;
  const isUpdatingCart = variantIdsBeingUpdated.has(id);

  useEffect(() => {
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);
  return (
    <>
      <div className={styles.productSummary} data-testid="product-summary">
        <div className={styles.galleryContainer}>
          {isSubscriptionAvailable && (
            <div className={styles.subscriptionBadge}>
              <SubscriptionBadge />
            </div>
          )}
          <Gallery
            images={galleryUrls}
            viewSize={viewSize}
            context="pdp"
            disableBodyScroll={disableBodyScroll}
            clearAllBodyScrollLocks={clearAllBodyScrollLocks}
          />
        </div>

        <div className={styles.detail}>
          <h2 className={styles.brandName}>{brandName}</h2>
          <h1 className={styles.productName}>{productName}</h1>
          {rateAndReviewFF && (
            <div className={styles.ratingAndCtaContainer}>
              <div className={styles.starRatingContainer}>
                <StarRatingDisplay
                  theme="pink"
                  rating={productRatingData.averageRating}
                  ratingCount={productRatingData.totalReviewCount}
                  size="large"
                />
              </div>
              <ProductReviewCTA
                loggedIn={loggedIn}
                productHasReviewed={hasProductReviewed}
                productHasReviews={productRatingData.totalReviewCount > 0}
                scrollToRef={reviewsSectionRef}
              />
            </div>
          )}
          {loggedIn || !campaignProductData || !isPublicShop ? (
            <NonSubscribableDetails retailPrice={retailPrice} />
          ) : (
            <SubscribableDetails
              id={id}
              productName={productName}
              price={retailPrice}
              salePrice={salePrice}
              percentOff={percentOff}
              sku={sku}
              soldOut={soldOut}
              planPrice={planPrice}
              shippingRestrictions={shippingRestrictions}
              ctaElementRef={ctaElementRef}
              isParisHiltonKit={isParisHiltonKit}
              isSubscriptionAvailable={isSubscriptionAvailable}
              isUpdatingCart={isUpdatingCart}
              addToPublicCart={addToPublicCart}
            />
          )}
          <div className={styles.variants}>
            <Variants
              variants={variants}
              selectedVariant={selectedVariant}
              selectedDescriptors={selectedDescriptors}
              updateSelectedVariant={updateSelectedVariant}
              updateSelectedDescriptors={updateSelectedDescriptors}
              context="pdp"
            />
          </div>
        </div>
      </div>
      <ReactTooltip className={styles.tooltip} type="dark" effect="solid" />
    </>
  );
};

ProductSummary.propTypes = {
  productName: PropTypes.string.isRequired,
  defaultVariant: PropTypes.shape({
    retailPrice: PropTypes.string,
    sku: PropTypes.string,
  }).isRequired,
  productRatingData: PropTypes.shape({
    averageRating: PropTypes.number,
    totalReviewCount: PropTypes.number,
  }),
  brandName: PropTypes.string.isRequired,
  variants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedVariant: PropTypes.shape({
    id: PropTypes.number,
    galleryUrls: PropTypes.arrayOf(PropTypes.string),
    isSubscriptionAvailable: PropTypes.bool,
  }).isRequired,
  selectedDescriptors: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateSelectedVariant: PropTypes.func.isRequired,
  updateSelectedDescriptors: PropTypes.func.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  viewSize: PropTypes.string.isRequired,
  campaignProductData: PropTypes.shape({
    subscribable: PropTypes.bool,
  }),
  rateAndReviewFF: PropTypes.bool.isRequired,
  planPrice: PropTypes.number.isRequired,
  shippingRestrictions: PropTypes.arrayOf(PropTypes.string).isRequired,
  ctaElementRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  hasProductReviewed: PropTypes.bool.isRequired,
  reviewsSectionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  isParisHiltonKit: PropTypes.bool.isRequired,
  variantIdsBeingUpdated: PropTypes.shape({}).isRequired,
  addToPublicCart: PropTypes.func.isRequired,
};

ProductSummary.defaultProps = {
  productRatingData: {},
  campaignProductData: null,
  reviewsSectionRef: null,
};

export default withSelectedVariant(false)(ProductSummary);
